import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getProviderUrl } from '../config/providerRegistry';
import { buildProviderImportStartPayload } from '../utils/providerImportPayload';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

function normalizeProviderKey(provider) {
  return provider?.toLowerCase().replace(/\s/g, '').replace(/-/g, '').replace(/_/g, '') || '';
}

export function getImportStartErrorMessage(error, fallback = 'Failed to start import') {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || String(item)).join(', ');
  }
  if (typeof error?.response?.data?.message === 'string' && error.response.data.message.trim()) {
    return error.response.data.message;
  }
  if (error?.response?.status) {
    return `Import failed (${error.response.status}). Please try again.`;
  }
  if (error?.message && error.message !== 'Network Error') {
    return error.message;
  }
  return fallback;
}

/**
 * Start a credit report import using saved provider credentials.
 * Uses POST /credit-report/import/start — not the settings connections route.
 */
export async function startSavedCredentialReportImport(provider) {
  const providerKey = normalizeProviderKey(provider);
  if (!providerKey) {
    throw new Error('Unable to determine provider');
  }

  const token = localStorage.getItem('fundelite_token');
  const response = await axios.post(
    `${API}/credit-report/import/start`,
    buildProviderImportStartPayload({
      provider: providerKey,
      useSavedCredentials: true,
      saveCredentials: false,
    }),
    { headers: { Authorization: `Bearer ${token}` } },
  );

  const id = response.data?.import_id;
  if (!id) throw new Error('No import ID returned');
  return { importId: id, data: response.data };
}

/**
 * Execute provider refresh/import actions using shared decision logic.
 */
export async function executeProviderRefreshAction({
  provider,
  actionState,
  hasCredentials = false,
  onImportStarted,
  onNeedCredentials,
  onViewReport,
}) {
  const action = actionState?.action;
  if (!provider || !action) return null;

  const providerKey = normalizeProviderKey(provider);

  if (action === 'view_report') {
    if (onViewReport) {
      onViewReport();
      return null;
    }
    if (!hasCredentials) {
      onNeedCredentials?.(providerKey);
      return null;
    }
    const { importId, data } = await startSavedCredentialReportImport(providerKey);
    onImportStarted?.(importId, data);
    return importId;
  }

  if (action === 'purchase') {
    const url = getProviderUrl(providerKey);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('Please visit your monitoring provider to purchase or upgrade your report.');
    }
    return null;
  }

  if (action === 'connect') {
    onNeedCredentials?.(providerKey);
    return null;
  }

  if ((action === 'refresh_now' || action === 'check') && !hasCredentials) {
    onNeedCredentials?.(providerKey);
    return null;
  }

  const { importId, data } = await startSavedCredentialReportImport(providerKey);
  onImportStarted?.(importId, data);
  return importId;
}

export function useProviderRefreshImport({
  provider,
  actionState,
  hasCredentials = false,
  activeImportId = null,
  onImportStarted,
  onNeedCredentials,
  onRefreshMetadataUpdated,
  onViewReport,
} = {}) {
  const [importing, setImporting] = useState(false);
  const [importId, setImportId] = useState(null);

  const startReimport = useCallback(async () => {
    if (activeImportId) return null;
    const { importId: id, data } = await startSavedCredentialReportImport(provider);
    setImportId(id);
    onImportStarted?.(id, data);
    return id;
  }, [activeImportId, provider, onImportStarted]);

  const executeAction = useCallback(async (overrideAction) => {
    const action = overrideAction || actionState?.action;
    if (!provider || !action || activeImportId) return null;

    const providerKey = normalizeProviderKey(provider);

    if (action === 'view_report') {
      if (onViewReport) {
        onViewReport();
        return null;
      }
      if (!hasCredentials) {
        onNeedCredentials?.(providerKey);
        return null;
      }
      setImporting(true);
      try {
        const { importId: id, data } = await startSavedCredentialReportImport(provider);
        setImportId(id);
        onImportStarted?.(id, data);
        onRefreshMetadataUpdated?.();
        return id;
      } catch (error) {
        const message = getImportStartErrorMessage(error);
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        if (
          status === 404
          || status === 400
          || (typeof detail === 'string' && detail.toLowerCase().includes('credential'))
        ) {
          onNeedCredentials?.(providerKey);
        } else {
          toast.error(message);
        }
        return null;
      } finally {
        setImporting(false);
      }
    }

    if (action === 'purchase') {
      const url = getProviderUrl(providerKey);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.info('Please visit your monitoring provider to purchase or upgrade your report.');
      }
      return null;
    }

    if (action === 'connect') {
      onNeedCredentials?.(providerKey);
      return null;
    }

    if ((action === 'refresh_now' || action === 'check') && !hasCredentials) {
      onNeedCredentials?.(providerKey);
      return null;
    }

    setImporting(true);
    try {
      const id = await executeProviderRefreshAction({
        provider,
        actionState: { ...actionState, action: overrideAction || actionState?.action },
        hasCredentials,
        onImportStarted: (nextImportId, data) => {
          setImportId(nextImportId);
          onImportStarted?.(nextImportId, data);
        },
        onNeedCredentials,
        onViewReport,
      });
      onRefreshMetadataUpdated?.();
      return id;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const message = getImportStartErrorMessage(error);
      if (
        status === 404
        || status === 400
        || (typeof detail === 'string' && detail.toLowerCase().includes('credential'))
      ) {
        onNeedCredentials?.(providerKey);
      } else {
        toast.error(message);
      }
      return null;
    } finally {
      setImporting(false);
    }
  }, [
    provider,
    actionState,
    hasCredentials,
    activeImportId,
    onNeedCredentials,
    onRefreshMetadataUpdated,
    onViewReport,
    onImportStarted,
  ]);

  return {
    importing,
    importId,
    executeAction,
    startReimport,
    setImportId,
  };
}

export default useProviderRefreshImport;
